import React, { useContext, useEffect, useState } from 'react';
import { VerticalSpace } from '@brokerbay/design-components';
import { INotificationSettings } from '@bb/users';
import {
  Divider,
  Radio,
  RadioGroup,
  Space,
  Switch,
  Text,
  Title,
} from 'components';
import { RadioChangeEvent } from 'antd';
import { AuthContext } from 'contexts';
import { featureEnabled } from 'utils';
import BasicCard from './BasicCard';

type ShowingUpdatesCardProps = {
  handleChange: (changes: Partial<INotificationSettings>) => void;
  notifications: INotificationSettings;
  setNotifications: React.Dispatch<React.SetStateAction<INotificationSettings>>;
  isLicensed?: boolean;
  hasSmsFeature?: boolean;
};

type DefaultMethodsType = 'email' | 'text' | 'office' | 'email&text';

const ShowingUpdatesCard: React.FC<ShowingUpdatesCardProps> = ({
  handleChange,
  notifications,
  setNotifications,
  isLicensed,
  hasSmsFeature,
}) => {
  const { auth } = useContext(AuthContext);
  const hasShowingSmsFeature =
    hasSmsFeature || auth?.org.features.includes('SHOWING_AGENT_SMS') || false;

  const [defaultMethod, setDefaultMethod] =
    useState<DefaultMethodsType>('email');

  const radioOptions: { label: string; value: string }[] = [
    { label: 'Email', value: 'email' },
    ...(hasShowingSmsFeature
      ? [
          { label: 'SMS', value: 'text' },
          { label: 'Email & SMS', value: 'email&text' },
        ]
      : []),
    { label: 'Office', value: 'office' },
  ];

  useEffect(() => {
    const { bookShowingDefault } = notifications.officeMessages;
    const trueKeys = Object.keys(bookShowingDefault).filter(
      key =>
        bookShowingDefault[key as Exclude<DefaultMethodsType, 'email&text'>],
    );
    if (
      !hasShowingSmsFeature &&
      (trueKeys.includes('text') || trueKeys.includes('email&text'))
    ) {
      setDefaultMethod('email');
    } else if (trueKeys.length > 1) {
      setDefaultMethod('email&text');
    } else {
      setDefaultMethod(
        trueKeys[0] as Exclude<DefaultMethodsType, 'email&text'>,
      );
    }
  }, [notifications, hasShowingSmsFeature]);

  const methodChangeHandler = (event: RadioChangeEvent) => {
    const selectedMethod = event.target.value;
    const bookShowingDefaultObj = {
      email: false,
      text: false,
      office: false,
    };
    if (selectedMethod === 'email&text') {
      bookShowingDefaultObj.email = true;
      bookShowingDefaultObj.text = true;
    } else {
      bookShowingDefaultObj[
        selectedMethod as Exclude<DefaultMethodsType, 'email&text'>
      ] = true;
    }
    setNotifications(prev => ({
      ...prev,
      officeMessages: {
        ...prev.officeMessages,
        bookShowingDefault: bookShowingDefaultObj,
      },
    }));
  };

  return (
    <BasicCard title="Showing Updates">
      <VerticalSpace size="small">
        {(isLicensed ?? auth?.isLicensed) && (
          <>
            <Title level={3}>Buyer Showing Updates</Title>
            <Text>
              Choose a default contact method when booking showings. This option
              will be chosen automatically when a showing is being booked for
              you:
            </Text>
            <RadioGroup value={defaultMethod} onChange={methodChangeHandler}>
              <VerticalSpace size={5}>
                {radioOptions.map(option => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    data-cy={`buyer-showing-updates-${option.value}`}
                  >
                    {option.label}
                  </Radio>
                ))}
              </VerticalSpace>
            </RadioGroup>
            <Divider />
          </>
        )}
        <Title level={3}>Listing Showing Updates</Title>
        <Text>
          Get notified when you have appointment requests, upcoming showing
          appointments, time change requests and cancellations.
        </Text>
        <VerticalSpace size={0}>
          <Space size={0}>
            <Switch
              size="small"
              data-cy="listing-showing-updates-email"
              checked={notifications.officeMessages.appointmentUpdates.email}
              onChange={checked =>
                handleChange({
                  officeMessages: {
                    ...notifications.officeMessages,
                    appointmentUpdates: {
                      ...notifications.officeMessages.appointmentUpdates,
                      email: checked,
                    },
                  },
                })
              }
            />{' '}
            Email
          </Space>
          <Space size={0}>
            <Switch
              size="small"
              data-cy="listing-showing-updates-sms"
              checked={notifications.officeMessages.appointmentUpdates.text}
              onChange={checked =>
                handleChange({
                  officeMessages: {
                    ...notifications.officeMessages,
                    appointmentUpdates: {
                      ...notifications.officeMessages.appointmentUpdates,
                      text: checked,
                    },
                  },
                })
              }
            />{' '}
            SMS
          </Space>
        </VerticalSpace>
        {featureEnabled('showingSettingsFeature') && (
          <>
            <Divider />
            <Title level={3}>Incomplete Listing Settings</Title>
            <Text>
              Get reminders to set up listings that are missing critical
              information.
            </Text>
            <VerticalSpace size={0}>
              <Space size={0}>
                <Switch
                  size="small"
                  data-cy="incomplete-listing-settings-email"
                  checked={notifications.unconfiguredListing === 'email'}
                  onChange={checked =>
                    handleChange({
                      unconfiguredListing: checked ? 'email' : 'none',
                    })
                  }
                />{' '}
                Email
              </Space>
            </VerticalSpace>
          </>
        )}
      </VerticalSpace>
    </BasicCard>
  );
};

export default ShowingUpdatesCard;
